import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Blocking modal shown when the Supabase session ends without an explicit
 * logout (token expired, refresh token rotated by a sign-in on another
 * device, etc.). Replaces the previous silent toast + auto-redirect to
 * /auth so the student can see/copy any in-progress work before
 * re-authenticating.
 *
 * AuthProvider intentionally keeps `user`/`profile` in state while
 * `sessionExpired` is true, so route guards do not unmount the page
 * underneath this dialog. Clicking "Log in again" calls
 * `acknowledgeSessionExpired()` which clears auth state and lets the
 * guards naturally redirect to /auth.
 */
export const SessionExpiredDialog: React.FC = () => {
  const { sessionExpired, acknowledgeSessionExpired } = useAuth();
  const { t } = useLanguage();

  return (
    <Dialog open={sessionExpired}>
      <DialogContent
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {t("Byli jste odhlášeni", "Boli ste odhlásení")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t(
              "Vaše relace skončila. Mohlo to být kvůli vypršení přihlášení nebo proto, že jste se přihlásili na jiném zařízení. Než budete pokračovat, můžete si zde zkopírovat rozepsanou práci. Poté se prosím přihlaste znovu.",
              "Vaša relácia sa skončila. Mohlo to byť kvôli vypršaniu prihlásenia alebo preto, že ste sa prihlásili na inom zariadení. Pred pokračovaním si tu môžete skopírovať rozpísanú prácu. Potom sa prosím prihláste znova."
            )}
          </p>
          <div className="flex justify-end">
            <Button onClick={acknowledgeSessionExpired}>
              {t("Přihlásit se znovu", "Prihlásiť sa znova")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
